import { mergeMap, scan, map, share, tap, takeUntil, throttleTime, takeWhile, pluck, filter, first } from 'rxjs/operators';
import { Observable, Subject, Subscription, BehaviorSubject, asyncScheduler, ReplaySubject } from 'rxjs';

import { GroupStatus, CacheProgress, ResourceState, ObservableValue, GroupStateMap, GroupState } from './progress-model';

interface ResourceStateMap<T> { [idx: number]: ResourceState<T> };

/**
* Generic resource progress accumulator and state handler to
* handle async progress events and combine them in groups and
* deliver a overall progress state.
*/
export class ProgressManager<T = any> {

    private readonly CONCURENT_DOWNLOADS = 3;

    private downloadQueue = new Subject<Observable<ResourceState<T>>>();
    private resourceErrorSubject = new Subject<ResourceState<T>>();

    private downloadProgressObs: Observable<CacheProgress> = null;
    private destroyDownload = new Subject<void>();

    // List of resources mapped in groups
    private resourceMap: GroupStateMap = {};
    private resourceMapSubject = new BehaviorSubject<GroupStateMap>({});
    private progressGroupSubject = new Subject<{ group: string, progress: CacheProgress, state: GroupState }>();

    // Subscriptions
    private downloadQueueSub: Subscription;

    constructor() {
    }

    public init() {
        // Generate a new download queue pipe
        let downloadQueueObs = this.genDownloadQueue();
        // Start download queue loob observer
        this.downloadQueueSub = downloadQueueObs.subscribe();
    }

    /**
    * Init or restore map of resources
    */
    public restoreResourceMap(resourceMap: GroupStateMap) {
        // console.log("Restore cache map ", resourceMap);
        this.resourceMap = resourceMap;
        this.resourceMapSubject.next(this.resourceMap);
    }

    /**
    * Get current resource state map
    */
    public getResourceMap(): GroupStateMap {
        return this.resourceMap;
    }

    /**
    * Add resources to processing progress (download/upload) queue
    */
    public addResources(resourceList: ObservableValue<ResourceState<T>>[]) {
        // Check resource integrity and warn if group is missing
        resourceList.forEach(resource => this.addResource(resource));
    }

    /**
    * Add resource to processing progress (download/upload) queue
    */
    public addResource(resource: ObservableValue<ResourceState<T>>) {
        if(!this.downloadQueueSub) console.warn("Please call init() to start progress machine");

        // Check if progress group exists
        let groupId = resource.lastVal.groupId;
        // Create resource group automatically if it dosent exist already
        // console.warn(`Unknown resource group '${groupId}', please add it.`);
        if(!this.getResourceGroup(groupId)) this.initResourceGroup(groupId).resourceList.push(resource);
        // Add resource to download queue
        this.downloadQueue.next(resource);
    }

    /**
    * Helper to create a resource with the given group id (call start to add to progress queue)
    */
    public createResource(resourceGroup: GroupState, url: string, name: string, moteKey = 'none'): ResourceState<T> {
        return new ResourceState<T>(url, name, resourceGroup.id, moteKey);
    }

    /**
    * Helper to add resources of group to progress queue and wait for group to finish
    */
    public start(resourceGroup: GroupState): Promise<GroupStateMap> {
        this.addResources(resourceGroup.resourceList);
        return this.awaitGroupDone(resourceGroup);
    }

    /**
    * Add a new resources group which is a collection of resources (ie a project) that needs to be processed
    */
    public initResourceGroup(groupId: string): GroupState {
        // Create new project cache state
        let projectState = new GroupState();
        projectState.id = groupId;
        projectState.abortCtrl = new AbortController();
        projectState.status = 'Idle';
        projectState.totalBytes = 0;
        projectState.totalPct = 0;

        // Save state in map and notify listners
        this.resourceMap[groupId] = projectState;
        this.resourceMapSubject.next(this.resourceMap);
        return this.resourceMap[groupId];
    }

    /**
    * Update resourcelist / state of resource group and notify listners
    */
    public updateResourceGroup(groupId, state: GroupState): GroupState {
        this.resourceMap[groupId] = state;
        this.resourceMapSubject.next(this.resourceMap);
        return this.resourceMap[groupId];
    }

    /**
    * Init or restore map of resources
    */
    public getResourceGroup(groupId: string) {
        return this.resourceMap[groupId];
    }

    /**
    * Cancel resource group
    */
    public cancelResourceGroup(groupId: string, abortStatus: GroupStatus = 'Cancled') {
        let resourceGroup = this.resourceMap[groupId];

        for(let resource of resourceGroup.resourceList) {
            // Cancel all actions in resource list
            resource.lastVal.status = 'Cancled';
            resource.lastVal.percentage = 100;
            resource.lastVal.bytesTotal = 0;
            resource.lastVal.bytesTransfered = 0;
        }

        // Abort all current ongoing actions (fetchs/uploads) of group
        resourceGroup.abortCtrl.abort();
        resourceGroup.status = abortStatus;
        resourceGroup.totalBytes = 0;
        //resourceGroup.resourceList = [];
        this.resourceMapSubject.next(this.resourceMap);
    }

    /**
    * Report fatal error and abort all resources
    * @param progressGroup
    */
    public reportError(name: string, msg: string) {
        console.error("Abort progress manager tasks (" + name + "): " + msg);
        // Abort all progress groups
        let groups = Object.keys(this.resourceMap);

        for(let id of groups) {
            if(this.resourceMap[id].status != 'Downloading') continue;
            // Cancel group
            this.cancelResourceGroup(id, 'Error');
        }
        this.resourceMapSubject.error({ name: name, msg: msg, lastStateMap: this.resourceMap });
    }

    /**
    * Wait for all resources in progress group to be finishes
    * @param progressGroup ProgressGroupr (CacheState) to wait for
    * @returns Promis (with current cache map) that resolves when the specified group have finished
    */
    public awaitGroupDone(progressGroup: GroupState): Promise<GroupStateMap> {
        return this.watchResourceMap().pipe(takeWhile((map: GroupStateMap) => map[progressGroup.id].status != 'Cached', true)).toPromise();
    }

    /**
    * Wait for all resources in progress group to be finishes and return reesult
    * @param progressGroup ProgressGroupr (CacheState) to wait for
    * @returns Promis (with group result list from current cache map) that resolves when the specified group has finished
    */
    public awaitGroupResult(progressGroup: GroupState): Promise<ResourceState<T>[]> {
        // Observable that emits resource map, each time a new map is ready and the group has finished
        let groupDone: Observable<GroupStateMap> = this.watchResourceMap().pipe(filter(map => map[progressGroup.id].status == 'Cached'));
        // Emit array of all last values (results) of group resources on first finish signal
        return groupDone.pipe(pluck(progressGroup.id), map(group => group.resourceList.map(obs => obs.lastVal)), first()).toPromise();
    }

    /**
     * Continuously watch the result of a group as it finishes multiple times
     */
    public watchGroupResult(progressGroup: GroupState): Observable<ResourceState<T>[]> {
        let statusDone = (status: GroupStatus) => (status == 'Cached' || status == 'Cancled' || status == 'Error');
        return this.progressGroupSubject.pipe(
            filter(ev => (ev.group == progressGroup.id && statusDone(ev.state.status))),
            map(ev => ev.progress.resources)
        );
    }

    /**
     * Continuously watch the result of all groups as it finishes multiple times
     */
    public watchGroupResults(): Observable<ResourceState<T>[]> {
        let statusDone = (status: GroupStatus) => (status == 'Cached' || status == 'Cancled' || status == 'Error');
        return this.progressGroupSubject.pipe(
            filter(ev => (statusDone(ev.state.status))),
            map(ev => ev.progress.resources)
        );
    }

    /**
     * Watch progress of a group of resources - returns a observable of resource list (this never terminated aka. will not terminate on first group done event)
     */
    public watchGroupProgress(progressGroup: GroupState): Observable<ResourceState<T>[]> {
        // Plubck the results of the group out
        return this.watchResourceMap().pipe(pluck(progressGroup.id), map(group => group.resourceList.map(obs => obs.lastVal)));
    }

    /**
    * Watch progress errors of resources (when resources fail to download or upload)
    */
    public watchResourceErrors(): Observable<ResourceState<T>> {
        return this.resourceErrorSubject.asObservable();
    }

    /**
    * Watch the state of all resources (ordered in a map of groups)
    */
    public watchResourceMap(): Observable<GroupStateMap> {
        return this.resourceMapSubject.asObservable();
    }

    /**
    * Watch progress of ongoing caching processes
    */
    public watchCachingProgress(): Observable<CacheProgress> {
        if(this.downloadProgressObs) return this.downloadProgressObs;

        // Number of queued downloads (only CONCURENT_DOWNLOADS of these will be concurrently downloaded)
        let queuedDownloads = 0;
        // Map of download results (results will only appear here when first fetch response comes back)
        let downloadListAcc: ResourceStateMap<T> = {};
        let lastResourceState: ResourceState<T> = null;

        // Define download pipe functions
        let queueCount = tap((obs) => queuedDownloads++);
        // Init pipes for file upload queue (merge results of 5 downloads concurrently)
        let mergeStates = mergeMap((a: Observable<ResourceState<T>>) => a, (ov, val, idx) => { return { idx: idx, val: val }; }, this.CONCURENT_DOWNLOADS);
        // Accumulate results in a result map with array index as key
        let stateMap = scan<{ idx: number; val: ResourceState<T> }, ResourceStateMap<T>>((acc, ev) => { acc[ev.idx] = ev.val; lastResourceState = ev.val; return acc; }, downloadListAcc);
        // Convert map to array of results
        let mapToArray = map(statusMap => Object.keys(statusMap).map(key => statusMap[key]));

        let calcTotalPct = map((resources: ResourceState<T>[]): CacheProgress => {
            // Sum of all resource download state percentages
            let accPct = resources.reduce((acc, state) => acc + (state.percentage || 0), 0);
            // Gen list of running and cached/finished resources
            let project = lastResourceState.groupId;
            let projectCache = this.resourceMap[project];
            // Gen list of running and cached/finished resources
            let running = projectCache.resourceList.filter(res => res.lastVal.status == "Downloading" || res.lastVal.status == "Idle");
            let cached = projectCache.resourceList.filter(res => res.lastVal.status == "Cached");

            // Calculate total mean pct
            let totalPct = (queuedDownloads <= 0) ? 100 : (accPct / queuedDownloads);
            // Make sure that we will never send a finish event twice due to rounding errors
            if(totalPct >= 100 && running.length > 0) totalPct = 99.5;

            // Calc total bytes downloaded (of all projects)
            let totalBytes = resources.reduce((acc, state) => acc + (state.bytesTransfered || 0), 0);
            // Generate load status message
            // let msg = lastResourceState ? "Saving " + Math.floor(totalPct) + "% - " + lastResourceState.name + " (" +this.toMegaBytes(lastResourceState.bytesTotal)+ " MB)" : "No resource?";
            let msg = lastResourceState ? "Saving " + Math.floor(totalPct) + "% (" + this.toMegaBytes(totalBytes) + " MB)" : "No resource?";

            // Generate progress event for listners
            let progressEv = { resources: resources, totalPct: totalPct, totalBytes: totalBytes, msg: msg, lastResourceState: lastResourceState };

            // This is run after the main progress calculation (in watchCachingProgress)
            this.updateGroupState(progressEv, running, cached);

            // If resource has error notify listners
            if(lastResourceState.status == 'Error') this.resourceErrorSubject.next(lastResourceState);

            if(totalPct >= 100 && running.length <= 0) {
                let mb = this.toMegaBytes(totalBytes) + " MB";
                msg = lastResourceState.status == 'Cancled' ? 'Cancled' : `Done (downloaded ${mb})`;

                console.info(`*** Caching process done (${mb}), queued: (${queuedDownloads}), resource cnt: (${resources.length}), pct: (${totalPct}), last (name/status): ${lastResourceState.name} / ${lastResourceState.status} `);
                // Reset download progress map
                Object.keys(downloadListAcc).forEach(key => delete downloadListAcc[key]);
                queuedDownloads = 0;
            }
            return progressEv;
        });

        // Create shared download progress observer pipe (and tap into queue to count added downloads)
        let downloadObs = this.downloadQueue.pipe(queueCount, mergeStates, stateMap, mapToArray, calcTotalPct, takeUntil(this.destroyDownload), share());
        this.downloadProgressObs = downloadObs;
        //this.downloadProgressObs = this.startDownload.pipe( switchMap( restart => downloadObs ), share() );
        return this.downloadProgressObs;
    }

    private updateGroupState(ev: CacheProgress, running: ObservableValue<ResourceState<T>>[], cached: ObservableValue<ResourceState<T>>[]) {
        let groupId = ev.lastResourceState.groupId;
        let groupMap = this.resourceMap[groupId];
        let resources = groupMap.resourceList;

        if(!groupMap) return console.error("Group not in cache state map?? ", groupId);

        // Update project status
        let groupStatus = ev.lastResourceState.status;
        groupMap.status = groupStatus == 'Cancled' ? 'Cancled' : running.length <= 0 ? "Cached" : "Downloading";

        // Calculate total bytes downloaded in the project of last update
        let totalGroupBytes = groupMap.status == 'Cancled' ? 0 : resources.reduce((acc, res) => acc + res.lastVal.bytesTotal, 0);
        groupMap.totalBytes = totalGroupBytes;
        // Estimated average percentage done
        groupMap.totalPct = (totalGroupBytes <= 0) ? 0 : resources.reduce((acc, res) => acc + res.lastVal.percentage, 0) / resources.length;
        // Notify listners about new project state
        this.resourceMapSubject.next(this.resourceMap);
        // Emit progress map of the group that was updated
        this.progressGroupSubject.next({ group: groupId, progress: ev, state: this.resourceMap[groupId] });
    }

    public toMegaBytes(bytes: number) {
        return (bytes / 1000000).toFixed(1);
    }

    private genDownloadQueue() {
        return this.watchCachingProgress();
    }

    public destroy() {
        if(this.downloadQueueSub) this.downloadQueueSub.unsubscribe();
    }
}
